import React, {
  useContext,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { PortalContext } from "App";
import { useTranslation } from "react-i18next";
import { Button, Stack, Typography } from "@mui/material";
import s from "./OfflineMerchantDetail.module.scss";
import EditIcon from "@mui/icons-material/Edit";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import {
  queryMerchantDetail,
  queryMerchantStatusOptions,
  queryDropdownOptions,
  queryMerchantOtherDetail,
  updateMerchant,
} from "./model";

// --- Different tabs' components --- //
import MerchantInfo from "./merchant-info/MerchantInfo";
import MerchantEmployee from "./merchant-employee/MerchantEmployee";
import MerchantConfig from "./merchant-config/MerchantConfig";
import MerchantProcessorMDR from "./merchant-processor-mdr/MerchantProcessorMDR";
import MerchantMaintenanceRecord from "./merchant-maintenance-record/MerchantMaintenanceRecord";
import MerchantSMEUsage from "./merchant-sme-usage/MerchantSMEUsage";
import MerchantManagementOfflineMerchantProcessorAccounts from "../offline-merchant-processor-accounts/OfflineMerchantProcessorAccounts";
// --- Different tabs' components --- //

import { spinning } from "./utils";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import Alert from "@mui/material/Alert";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import moment from "moment";
import { isNullish } from "utils/utils";
import { useSnackbar } from "notistack";

const OfflineMerchantDetail = ({ path, redirectFn }) => {
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState("info");
  const [merchantId, setMerchantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [
    merchantExternalStatusSubmitting,
    setMerchantExternalStatusSubmitting,
  ] = useState(false);
  const [merchant, setMerchant] = useState({});
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);
  const [
    isExternalMerchantStatusDialogOpen,
    setIsExternalMerchantStatusDialogOpen,
  ] = useState(false);
  const externalMerchantStatusPrcessor = useRef(null);
  const [statusRemarks, setStatusRemarks] = useState();
  const [serviceIntegratorOptions, setServiceIntegratorOptions] = useState();
  const [merchantOtherDetail, setMerchantOtherDetail] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState();
  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };
  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );
  const DIALOG_LOCALE_KEY = "page.dialogs.";
  const MERCHANT_LOCALE_TABS_KEY = "page.merchants.tabs";
  const MERCHANT_LOCALE_KEY = "page.merchants.";
  const { enqueueSnackbar } = useSnackbar();
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const staticMerchantPaylaod = useCallback(
    (merchant, tab, specialCheckFields) => {
      const payload = {
        merchantId: merchant?.merchantId,
        logoPath: merchant?.logoPath,
        ciNumber: merchant?.ciNumber,
        phone: merchant?.phone,
        businessEstablishedDate: moment(merchant?.businessEstablishedDate),
        serviceIntegrator: merchant?.serviceIntegrator,
        summaryReportTime: moment(merchant?.summaryReportTime),
        otherInfo: merchant?.otherInfo,
        externalMerchantStatus: merchant?.externalMerchantStatus,
      };
      if (
        !tab ||
        (tab === "config" && specialCheckFields?.previewUrl) ||
        tab === "info"
      ) {
        payload.largeImagePath = merchant?.largeImagePath;
      }
      return payload;
    },
    [merchant],
  );

  // -------- Handle Status dialog -------- //
  const openAccountStatusDialog = (data) => {
    setAccountStatusDialogData(data);
    setIsAccountStatusDialogOpen(true);
  };

  const closeAccountStatusDialog = () => {
    restoreAccountStatusDialog();
    setIsAccountStatusDialogOpen(false);
  };

  const restoreAccountStatusDialog = () => {
    setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
  };

  const updatePayload = (fieldInput) => {
    setAccountStatusDialogData(fieldInput);
  };

  const AccountStatusDialogContent = () => {
    return (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={updatePayload}
      />
    );
  };

  const accountStatusDialogValidationCheck = () => {
    if (accountStatusDialogData.status !== "ACTIVE") {
      const validationResult =
        !!accountStatusDialogData.reason &&
        !!accountStatusDialogData.reason !== "";
      if (!validationResult) {
        snackbar(
          t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
            " Reason must not be empty!",
          "error",
        );
      }
      return validationResult;
    }
    return true;
  };

  const updateResultCallback = async () => {
    if (accountStatusDialogValidationCheck()) {
      setSubmitting(true);
      const reasonList = statusRemarks[accountStatusDialogData.status];
      const remarkOthersKey = "OTHERS";

      let remarksend =
        reasonList && (reasonList[accountStatusDialogData.reason] ?? "");
      if (accountStatusDialogData.reason == remarkOthersKey)
        remarksend = accountStatusDialogData.remark;

      let payload = {
        ...staticMerchantPaylaod(merchant),
        merchantId,
        isStatusChange: accountStatusDialogData.status != merchant.status,
        status: accountStatusDialogData.status,
      };

      if (!isNullish(accountStatusDialogData.reason))
        payload = {
          ...payload,
          lockAccountType: accountStatusDialogData.reason,
        };
      if (!isNullish(remarksend)) payload = { ...payload, remark: remarksend };

      const apiResponse = await updateMerchant(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout();
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackbar(
                t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            } else {
              snackbar(
                t(
                  DIALOG_LOCALE_KEY +
                    "changeStatus.msgSuccess." +
                    accountStatusDialogData.status.toLowerCase(),
                ),
                "success",
              );
            }
          }
        }
      }
      setSubmitting(false);
      closeAccountStatusDialog();
      await fetchMerchantDetail();
    }
  };

  const handleDeleteExternalMerchantStatus = async (processorBeingDeleted) => {
    setMerchantExternalStatusSubmitting(true);
    console.log("processorBeingDeleted", processorBeingDeleted);
    console.log("merchant", merchant);
    const externalMerchantStatus = Object.keys(
      merchant?.externalMerchantStatus,
    ).reduce((acc, key) => {
      if (key !== processorBeingDeleted) {
        acc[key] = merchant?.externalMerchantStatus[key];
      }
      return acc;
    }, {});
    try {
      const payload = {
        ...staticMerchantPaylaod(merchant),
        merchantId,
        externalMerchantStatus: externalMerchantStatus,
      };
      const apiResponse = await updateMerchant(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout();
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackbar(
                t(
                  MERCHANT_LOCALE_KEY +
                    "merchantDetail.fetchMessage.updateFailed",
                ),
                "error",
              );
            } else {
              snackbar(
                t(
                  MERCHANT_LOCALE_KEY +
                    "merchantDetail.fetchMessage.updateSuccess",
                ),
                "success",
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setMerchantExternalStatusSubmitting(false);
      setIsExternalMerchantStatusDialogOpen(false);
      fetchMerchantDetail();
    }
  };

  // -------- Handle Status dialog -------- //

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setMerchantId(detailPageState[path].merchantId);
    } else if (sessionState && sessionState[path]) {
      setMerchantId(sessionState[path].merchantId);
    } else {
      if (merchantId == "") setMerchantId("close");
    }
  }, [detailPageState[path]]);

  const fetchMerchantDetail = async () => {
    console.log("fetchMerchantDetail merchantId: ", merchantId);
    try {
      setLoading(true);
      const merchantResponse = await queryMerchantDetail(merchantId);
      const { data } = merchantResponse;
      if (data?.responseCode === "0000" && data?.data) {
        setMerchant(data?.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantAndMerchantOtherDetail = async () => {
    try {
      const [merchantResponse, merchantOtherDetailResp] = await Promise.all([
        queryMerchantDetail(merchantId),
        queryMerchantOtherDetail({ merchantId }),
      ]);
      const { data } = merchantResponse;
      if (data?.responseCode === "0000" && data?.data) {
        setMerchant(data?.data);
      }
      const { data: merchantOtherDetail } = merchantOtherDetailResp;
      if (
        merchantOtherDetail?.responseCode === "0000" &&
        merchantOtherDetail?.data
      ) {
        setMerchantOtherDetail(merchantOtherDetail?.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const initialFetchDetails = async () => {
    try {
      setLoading(true);
      const [
        merchantResponse,
        statusResponse,
        dropdownOptionsResp,
        merchantOtherDetailResp,
      ] = await Promise.all([
        queryMerchantDetail(merchantId),
        queryMerchantStatusOptions(),
        queryDropdownOptions(),
        queryMerchantOtherDetail({ merchantId }),
      ]);

      const { data } = merchantResponse;
      if (data?.responseCode === "0000" && data?.data) {
        setMerchant(data?.data);
        setStatusRemarks(statusResponse?.data?.data);
        setServiceIntegratorOptions(
          dropdownOptionsResp?.data?.data?.serviceIntegratorList,
        );
        setMerchantOtherDetail(merchantOtherDetailResp?.data?.data);
        setDropdownOptions(dropdownOptionsResp.data.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (merchantId) {
      initialFetchDetails();
    }
  }, [merchantId]);

  const tabStyle = {
    "&.Mui-selected": {
      color: "#309ea7",
    },
    textTransform: "none",
  };

  const tabs = () => (
    <Stack>
      <TabContext value={tabValue}>
        <Box
          sx={{
            borderBottom: 3,
            borderColor: "divider",
            background: "white",
          }}
        >
          <TabList
            onChange={(event, newValue) => {
              setTabValue(newValue);
            }}
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#309ea7",
              },
              "& .MuiTab-root": {
                whiteSpace: "nowrap",
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.info`)}
              value="info"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.employee`)}
              value="employee"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.config`)}
              value="config"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.processorMdr`)}
              value="processorMdr"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.processorAccounts`)}
              value="processorAccounts"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.maintenanceRecord`)}
              value="maintenanceRecord"
              sx={tabStyle}
            />
            <Tab
              label={t(`${MERCHANT_LOCALE_TABS_KEY}.smeUsage`)}
              value="smeUsage"
              sx={tabStyle}
            />
          </TabList>
        </Box>

        {loading ? (
          spinning()
        ) : (
          <>
            <TabPanel value="info">
              <MerchantInfo
                merchant={merchant}
                serviceIntegratorOptions={serviceIntegratorOptions}
                setMerchant={setMerchant}
                setMerchantOtherDetail={setMerchantOtherDetail}
                merchantOtherDetail={merchantOtherDetail}
                dropdownOptions={dropdownOptions}
                staticMerchantPaylaod={staticMerchantPaylaod}
                fetchMerchantAndMerchantOtherDetail={
                  fetchMerchantAndMerchantOtherDetail
                }
              />
            </TabPanel>
            <TabPanel value="employee">
              <MerchantEmployee
                merchantId={merchantId}
                redirectFn={redirectFn}
              />
            </TabPanel>
            <TabPanel value="config">
              <MerchantConfig
                merchant={merchant}
                merchantOtherDetail={merchantOtherDetail}
                setMerchant={setMerchant}
                setMerchantOtherDetail={setMerchantOtherDetail}
                fetchMerchantAndMerchantOtherDetail={
                  fetchMerchantAndMerchantOtherDetail
                }
                staticMerchantPaylaod={staticMerchantPaylaod}
              />
            </TabPanel>
            <TabPanel value="processorMdr">
              <MerchantProcessorMDR
                merchantId={merchantId}
                fetchMerchantDetail={fetchMerchantDetail}
                mccOptions={dropdownOptions?.mccList}
                merchant={merchant}
              />
            </TabPanel>
            <TabPanel value="processorAccounts">
              <MerchantManagementOfflineMerchantProcessorAccounts
                merchantId={merchantId}
                fetchMerchantDetail={fetchMerchantDetail}
              />
            </TabPanel>
            <TabPanel value="maintenanceRecord">
              <MerchantMaintenanceRecord
                merchantId={merchantId}
                serviceIntegratorOptions={serviceIntegratorOptions}
              />
            </TabPanel>
            <TabPanel value="smeUsage">
              <MerchantSMEUsage merchantId={merchantId} />
            </TabPanel>
          </>
        )}
      </TabContext>
    </Stack>
  );

  const warning = (merchant) => {
    const { status, remark, terminatedDate, sfDetail, externalMerchantStatus } =
      merchant;

    const alertList = [];

    const ErrorAlert = ({ id, description, processor }) => (
      <Alert
        key={id}
        sx={{
          marginTop: "10px",
          borderRadius: "8px",
          border: "1px solid #f44336",
        }}
        severity="error"
        icon={<CancelOutlinedIcon fontSize="medium" />}
        action={
          processor ? (
            <Button
              aria-label="close"
              color="inherit"
              size="medium"
              onClick={() => {
                setIsExternalMerchantStatusDialogOpen(true);
                externalMerchantStatusPrcessor.current = processor;
              }}
            >
              x
            </Button>
          ) : (
            <></>
          )
        }
      >
        <Typography>{description}</Typography>
      </Alert>
    );

    // Account status warning
    if (["TERMINATED", "SUSPENDED"].includes(status)) {
      alertList.push(
        <ErrorAlert
          id="status-alert"
          description={`Account is ${status.toLowerCase()}. Reason: ${remark} (${moment(terminatedDate).format("YYYY-MM-DD HH:mm:ss")})`}
        />,
      );
    }

    // SF account failure warning
    const parsedSf = sfDetail && JSON.parse(sfDetail)?.[0];
    if (parsedSf?.status === "FAILED") {
      alertList.push(
        <ErrorAlert
          id="sf-alert"
          description={`SF child account creation failure. Reason: ${parsedSf.remarks?.message}`}
        />,
      );
    }

    // Processor failures warning
    externalMerchantStatus &&
      Object.entries(externalMerchantStatus).forEach(([processor, value]) => {
        alertList.push(
          <ErrorAlert
            id={processor}
            description={
              `${processor} ` + value?.message || "account creation failure."
            }
            processor={processor}
          />,
        );
      });

    return <>{alertList}</>;
  };

  const header = () => (
    <Stack>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t(`${MERCHANT_LOCALE_KEY}merchantDetail.header.merchantName`)}:{" "}
        {merchant?.name}
      </Typography>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography>
          {t(`${MERCHANT_LOCALE_KEY}merchantDetail.header.merchantId`)}:{" "}
          {merchantId}
        </Typography>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          {t(`${MERCHANT_LOCALE_KEY}merchantDetail.header.status`)}
          <Stack
            className={[
              s.statusContainer,
              merchant?.status === "SUSPENDED"
                ? s.statusSuspended
                : merchant?.status === "TERMINATED"
                  ? s.statusTerminated
                  : s.statusActive,
            ]}
          >
            {merchant?.status}
          </Stack>
          <Button
            sx={{ display: merchant?.status === "TERMINATED" ? "none" : "" }}
            onClick={() => {
              openAccountStatusDialog(merchant);
            }}
          >
            <EditIcon sx={{ color: "#309ea7" }} />
          </Button>
        </Stack>
      </Stack>
      <Stack>{warning(merchant)}</Stack>
    </Stack>
  );

  return (
    <Stack
      sx={{
        padding: "24px",
        background: "white",
      }}
      gap={2}
    >
      {header()}
      {tabs()}

      {/* The merchant status handler */}
      <SimpleDialog
        open={isAccountStatusDialogOpen}
        dialogOncloseFn={closeAccountStatusDialog}
        title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
        content={AccountStatusDialogContent()}
        positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
        negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
        positiveActionLoading={submitting}
        negativeActionDisabled={submitting}
        confirmFn={
          !submitting
            ? updateResultCallback
            : () => {
                console.log("submitting?", submitting);
              }
        }
      />

      <SimpleDialog
        open={isExternalMerchantStatusDialogOpen}
        dialogOncloseFn={() => {
          setIsExternalMerchantStatusDialogOpen(false);
        }}
        title={t(
          `${MERCHANT_LOCALE_KEY}merchantDetail.header.errorDialog.title`,
        )}
        content={`Confirm ${externalMerchantStatusPrcessor.current} account setup success?`}
        positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
        negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
        positiveActionLoading={merchantExternalStatusSubmitting}
        negativeActionDisabled={merchantExternalStatusSubmitting}
        confirmFn={() => {
          handleDeleteExternalMerchantStatus(
            externalMerchantStatusPrcessor.current,
          );
        }}
      />
    </Stack>
  );
};

export default React.memo(OfflineMerchantDetail);
